import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Plus, Pencil, Trash2, ShieldCheck, Save, X, Eye, EyeOff, Search, Zap } from "lucide-react";
import { formatDate } from "@/lib/qa";
import { toast } from "sonner";

const EMPTY = {
    title: "", answer: "", category_id: "", sort_order: 0,
    // Optional enrichment fields (retro-compatible)
    question: "", real_problem: "", alt_phrases: "", tags: "",
};

// Convert one-per-line textarea to string[] and vice-versa
const linesToArr = (s) => (s || "").split("\n").map(x => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");

export default function AdminFaqs() {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [status, setStatus] = useState("all");
    const [category, setCategory] = useState("all");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    useEffect(() => { API.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);
    useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 300); return () => clearTimeout(t); }, [search]);

    const load = () => {
        setLoading(true);
        const params = {};
        if (status !== "all") params.status = status;
        if (category !== "all") params.category = category;
        if (debounced) params.search = debounced;
        API.get("/admin/faqs", { params }).then(({ data }) => setItems(data))
            .catch(e => setError(apiError(e))).finally(() => setLoading(false));
    };
    useEffect(load, [status, category, debounced]);

    const startNew = () => { setForm({ ...EMPTY, sort_order: items.length + 1 }); setEditing("new"); };
    const startEdit = (f) => {
        setForm({
            title: f.title, answer: f.answer, category_id: f.category_id, sort_order: f.sort_order || 0,
            question: f.question || "",
            real_problem: f.real_problem || "",
            alt_phrases: arrToLines(f.alt_phrases),
            tags: arrToLines(f.tags),
        });
        setEditing(f.id);
    };
    const cancel = () => { setEditing(null); setForm(EMPTY); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                title: form.title,
                answer: form.answer,
                category_id: form.category_id,
                sort_order: form.sort_order,
                question: form.question?.trim() || null,
                real_problem: form.real_problem?.trim() || null,
                alt_phrases: linesToArr(form.alt_phrases),
                tags: linesToArr(form.tags),
            };
            if (editing === "new") { await API.post("/admin/faqs", payload); toast.success("FAQ creata"); }
            else { await API.patch(`/admin/faqs/${editing}`, payload); toast.success("FAQ aggiornata"); }
            cancel(); load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    const togglePublish = async (f) => {
        try {
            const url = f.status === "published" ? `/admin/faqs/${f.id}/unpublish` : `/admin/faqs/${f.id}/publish`;
            await API.post(url);
            toast.success(f.status === "published" ? "FAQ nascosta" : "FAQ pubblicata");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };
    const remove = async (id) => {
        try { await API.delete(`/admin/faqs/${id}`); toast.success("FAQ eliminata"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const missing = [];
    if (form.title.length < 5) missing.push(`titolo min 5 (${form.title.length}/5)`);
    if (form.answer.length < 10) missing.push(`risposta min 10 (${form.answer.length}/10)`);
    if (!form.category_id) missing.push("scegli una categoria");
    const canSave = missing.length === 0 && !saving;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
            <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
                <Link to="/admin"><ArrowLeft className="size-4 mr-1" /> Panoramica admin</Link>
            </Button>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold inline-flex items-center gap-1.5">
                <ShieldCheck className="size-3.5" /> Pannello amministratore
            </div>
            <div className="mt-1 flex items-center justify-between">
                <h1 className="font-serif text-3xl font-semibold inline-flex items-center gap-2" data-testid="admin-faqs-title">
                    <Zap className="size-6 text-primary" /> Domande e risposte veloci
                </h1>
                {editing === null && <Button onClick={startNew} data-testid="admin-faqs-new"><Plus className="size-4 mr-2" /> Nuova FAQ</Button>}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Biblioteca di risposte immediate, gestita da te e visibile pubblicamente sotto <span className="font-mono">/faq</span> quando pubblicata.</p>

            {error && <Alert variant="destructive" className="mt-4"><AlertDescription>{error}</AlertDescription></Alert>}

            {editing !== null && (
                <Card className="mt-6 border-primary/30" data-testid="admin-faqs-form">
                    <CardContent className="pt-6 space-y-4">
                        <div className="space-y-2">
                            <Label>Titolo (domanda) * <span className="text-xs text-muted-foreground">({form.title.length}/200)</span></Label>
                            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} maxLength={200} data-testid="faq-title" placeholder="Es. Quando è obbligatoria l'assemblea condominiale?" />
                        </div>
                        <div className="space-y-2">
                            <Label>Risposta breve * <span className="text-xs text-muted-foreground">({form.answer.length}/2000)</span></Label>
                            <Textarea value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })} rows={5} maxLength={2000} data-testid="faq-answer" placeholder="Risposta chiara e concisa (le nuove righe vengono preservate)…" />
                        </div>
                        <div className="grid gap-3 md:grid-cols-[1fr_120px]">
                            <div className="space-y-2">
                                <Label>Categoria *</Label>
                                <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
                                    <SelectTrigger data-testid="faq-category"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                                    <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Ordine</Label>
                                <Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })} data-testid="faq-order" />
                            </div>
                        </div>

                        {/* ---- Enrichment (facoltativo) — visibile solo all'admin ---- */}
                        <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="faq-enrichment">
                            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Arricchimento ricerca (facoltativo, privato)</div>
                            <div className="space-y-2">
                                <Label>Domanda estesa <span className="text-xs text-muted-foreground">(peso ricerca ×7)</span></Label>
                                <Textarea value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} rows={2} maxLength={1000} data-testid="faq-question" placeholder="Come i condomini formulerebbero la domanda in linguaggio naturale…" />
                            </div>
                            <div className="space-y-2">
                                <Label>Problema reale risolto <span className="text-xs text-muted-foreground">(peso ricerca ×10, privato)</span></Label>
                                <Textarea value={form.real_problem} onChange={e => setForm({ ...form, real_problem: e.target.value })} rows={2} maxLength={500} data-testid="faq-real-problem" placeholder="Es. L'amministratore non convoca l'assemblea nonostante la richiesta dei condomini." />
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                                <div className="space-y-2">
                                    <Label>Parole/frasi dei condomini <span className="text-xs text-muted-foreground">(uno per riga, peso ×10, privato)</span></Label>
                                    <Textarea value={form.alt_phrases} onChange={e => setForm({ ...form, alt_phrases: e.target.value })} rows={4} data-testid="faq-alt-phrases" placeholder={"amministratore non risponde\nassemblea straordinaria\nautoconvocazione\narticolo 66"} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Tag <span className="text-xs text-muted-foreground">(uno per riga, peso ×4)</span></Label>
                                    <Textarea value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} rows={4} data-testid="faq-tags" placeholder={"assemblea\nconvocazione\nautoconvocazione"} />
                                </div>
                            </div>
                        </div>

                        {missing.length > 0 && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                Per salvare: {missing.join(" · ")}.
                            </p>
                        )}
                        <div className="flex justify-end gap-2 pt-2 border-t">
                            <Button variant="ghost" onClick={cancel}><X className="size-4 mr-1" /> Annulla</Button>
                            <Button onClick={save} disabled={!canSave} data-testid="faq-save">
                                {saving ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
                                Salva
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card className="mt-6 border-slate-200">
                <CardContent className="pt-5">
                    <div className="grid gap-3 md:grid-cols-3">
                        <div className="relative">
                            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca…" className="pl-9" data-testid="admin-faqs-search" />
                        </div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="admin-faqs-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                <SelectItem value="draft">Non pubblicate</SelectItem>
                                <SelectItem value="published">Pubblicate</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="admin-faqs-category-filter"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutte le categorie</SelectItem>
                                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <div className="mt-4">
                {loading ? <div className="text-center py-16"><Loader2 className="size-6 mx-auto animate-spin text-muted-foreground" /></div>
                    : items.length === 0 ? (
                        <Card className="border-dashed border-2 bg-muted/30"><CardContent className="py-14 text-center">
                            <Zap className="size-10 mx-auto text-muted-foreground/40" />
                            <p className="mt-3 text-muted-foreground">Nessuna FAQ ancora. Iniziamo?</p>
                            {editing === null && <Button onClick={startNew} className="mt-5"><Plus className="size-4 mr-2" /> Crea la prima FAQ</Button>}
                        </CardContent></Card>
                    ) : (
                        <div className="space-y-3" data-testid="admin-faqs-list">
                            {items.map(f => (
                                <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`admin-faq-${f.id}`}>
                                    <div className="flex items-start gap-4">
                                        <span className="size-8 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center shrink-0">{f.sort_order}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                {f.category && <span className="text-xs font-semibold text-primary uppercase tracking-widest">{f.category.name}</span>}
                                                <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${f.status === "published" ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                                                    {f.status === "published" ? "Pubblicata" : "Non pubblicata"}
                                                </span>
                                            </div>
                                            <div className="font-semibold text-slate-900">{f.title}</div>
                                            <p className="mt-1 text-sm text-slate-600 line-clamp-2">{f.answer}</p>
                                            <div className="mt-2 text-xs text-muted-foreground">Creata il {formatDate(f.created_at)} · Modificata il {formatDate(f.updated_at)}</div>
                                        </div>
                                        <div className="flex gap-1 shrink-0">
                                            <Button size="sm" variant="ghost" onClick={() => togglePublish(f)} data-testid={`faq-toggle-${f.id}`} title={f.status === "published" ? "Nascondi" : "Pubblica"}>
                                                {f.status === "published" ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => startEdit(f)} data-testid={`faq-edit-${f.id}`}><Pencil className="size-4" /></Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`faq-delete-${f.id}`}><Trash2 className="size-4" /></Button></AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Eliminare "{f.title}"?</AlertDialogTitle>
                                                        <AlertDialogDescription>Operazione irreversibile.</AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => remove(f.id)} className="bg-destructive text-destructive-foreground">Elimina</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>
        </div>
    );
}
